import { supabase } from '@/lib/supabase';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * EMAIL DOMAIN AUTHENTICATION — single source of truth for the front end.
 * ───────────────────────────────────────────────────────────────────────────
 * The live checking happens server-side in the `email-domain-verify` edge
 * function (real DNS over two independent DoH resolvers). This module only
 * describes the contract + the exact DNS records an operator has to publish,
 * so the admin UI and the verifier can never disagree about what "aligned"
 * means.
 *
 * WHY THIS IS THE #1 PRE-LAUNCH EMAIL TASK
 * giggone.com publishes an ENFORCING DMARC policy (p=quarantine). DMARC only
 * passes when SPF or DKIM both authenticate AND align with the From: domain.
 * Today neither aligns for platform mail, so a message sent as
 * "@giggone.com" is explicitly told to be quarantined by giggone.com's own
 * policy — the template has nothing to do with it.
 *
 * Until the records below are published the platform keeps the visible From:
 * on the provider domain (which is signed + aligned), with Reply-To
 * support@giggone.com. That authenticates cleanly and reaches the inbox. The
 * moment verification passes, the From: is auto-promoted to @giggone.com.
 */

export const BRAND_DOMAIN = 'giggone.com';
export const PROVIDER_DOMAIN = 't.ecom-email.com';
export const PROVIDER_SPF_INCLUDE = 'mailgun.org';
/** Real selector issued by the sending provider for giggone.com (confirmed live). */
export const DKIM_SELECTOR = 'smtp';


export type AuthStatus = 'pass' | 'warn' | 'fail';

export interface AlignmentTask {
  id: string;
  order: number;
  title: string;
  status: AuthStatus;
  blocking: boolean;
  detail: string;
  fix: string;
}

export interface IdentityCheck {
  address: string;
  displayName: string;
  spf: AuthStatus;
  dkim: AuthStatus;
  dmarc: AuthStatus;
  replyable: boolean;
  sendingAs: string;
  status: AuthStatus;
  note: string;
}

export interface DomainVerifyResult {
  success: boolean;
  domain: string;
  providerDomain: string;
  checkedAt: string;
  readOnly: boolean;
  allAligned: boolean;
  overall: number;
  providerScores: { gmail: number; outlook: number; yahoo: number; apple: number };
  projectedScores: { gmail: number; outlook: number; yahoo: number; apple: number };
  spf: any;
  dkim: any;
  dmarc: any;
  bounce: any;
  mx: any;
  identities: IdentityCheck[];
  reputation: any;
  tasks: AlignmentTask[];
  activeFromDomain: string;
  promoted: boolean;
  inboxExpectation: string;
  error?: string;
}

/**
 * Run the live alignment check. Any signed-in user may READ the result (it is
 * public DNS); only an administrator's run persists the audit and promotes the
 * From: domain.
 */
export const verifyEmailDomain = async (persist = true): Promise<DomainVerifyResult | { error: string }> => {
  const { data, error } = await supabase.functions.invoke('email-domain-verify', { body: { persist } });
  if (error) return { error: error.message };
  if (data?.error) return { error: data.error };
  return data as DomainVerifyResult;
};

export interface DomainConfig {
  id: number;
  brand_domain: string;
  provider_domain: string;
  active_from_domain: string;
  /** Domain registered with the sending provider. The ROOT, for strict alignment. */
  sending_domain: string;
  /** Where DMARC aggregate reports go. MUST be a mailbox that actually exists. */
  dmarc_rua: string;
  /** GoHighLevel's dedicated sending subdomain — recorded so nothing reuses it. */
  ghl_subdomain: string;
  spf_ok: boolean;
  dkim_ok: boolean;
  dmarc_ok: boolean;
  bounce_ok: boolean;
  identities_ok: boolean;
  auto_promote: boolean;
  promoted_at: string | null;
  last_checked_at: string | null;
}


export const fetchDomainConfig = async (): Promise<DomainConfig | null> => {
  const { data } = await supabase
    .from('mkt_email_domain_config')
    .select('*')
    .order('id', { ascending: true })
    .limit(1);
  return (data?.[0] as DomainConfig) || null;
};

/** Turn auto-promotion of the branded From: domain on/off (admin only via RLS). */
export const setAutoPromote = async (id: number, on: boolean) => {
  const { error } = await supabase.from('mkt_email_domain_config').update({ auto_promote: on }).eq('id', id);
  return error ? { error: error.message } : { success: true };
};

/* ───────────────────────────────────────────────────────────────────────────
 * GODADDY IMPLEMENTATION GUIDE
 * The live values (including the REAL 2048-bit DKIM public key issued for
 * giggone.com) come from the `email-dns-guide` edge function, which reads them
 * from the sending provider and compares them with what is published today, so
 * the panel can say ADD vs REPLACE per record instead of guessing.
 * ─────────────────────────────────────────────────────────────────────────── */

export type RecordAction = 'add' | 'replace' | 'already-correct' | 'skip';

export interface GuideRecord {
  step: number;
  id: string;
  priority: 'critical' | 'high' | 'medium';
  required: boolean;
  /** MX appears only as a "do not change" warning row, never as something to publish. */
  type: 'TXT' | 'CNAME' | 'MX';

  /** Exactly what goes in GoDaddy's "Name"/Host box (GoDaddy appends the domain). */
  godaddyHost: string;
  fqdn: string;
  value: string;
  ttl: string;
  action: RecordAction;
  existing: string | null;
  title: string;
  note: string;
  verify: string;
}

/** A record in the zone that belongs to another system and must not be touched. */
export interface CoexistItem {
  type: string;
  host: string;
  fqdn: string;
  value: string | null;
  verdict: 'keep';
  why: string;
}

/**
 * mail.giggone.com is a GoHighLevel / LeadConnector dedicated sending domain.
 * Its DKIM private key lives in GoHighLevel's account, so it can never be
 * reused by us — and because it is a different FQDN it can never collide with
 * our own key either. The verifier reports it so nobody deletes it.
 */
export interface Coexistence {
  detected: boolean;
  subdomain: string;
  owner: string;
  confidence: 'confirmed' | 'likely' | 'not-found';
  verdict: string;
  reason: string;
  items: CoexistItem[];
}

/** Plain-language answer to a setup decision, with the reasoning kept attached. */
export interface SetupDecision {
  q: string;
  a: string;
  why: string;
}

/**
 * DMARC aggregate reports are silently discarded when `rua` points at a mailbox
 * that does not exist, so the destination is configurable and its
 * deliverability is checked against the domain's live MX.
 */
export interface DmarcMailbox {
  address: string;
  deliverable: boolean;
  domainMx: string[];
  note: string;
  alternatives: string[];
}

export interface DnsGuideResult {
  success: boolean;
  domain: string;
  /** Domain registered with the sending provider — the ROOT, for strict alignment. */
  sendingDomain: string;
  providerDomain: string;
  registrar: string;
  checkedAt: string;
  provider: { connected: boolean; source: string; state: string | null; hasLiveDkimKey: boolean; error: string | null };
  dmarcMailbox: DmarcMailbox;
  published: {
    rootTxt: string[];
    rootMx: string[];
    spf: string | null;
    spfRecordCount: number;
    dmarc: string | null;
    dkim: string | null;
    mgCname: string | null;
    emailCname: string | null;
  };
  coexistence: Coexistence;
  decisions: SetupDecision[];
  records: GuideRecord[];
  outstanding: number;
  propagation: string;
  error?: string;
}

/** Pull the live, copy-and-paste GoDaddy record set. */
export const fetchDnsGuide = async (): Promise<DnsGuideResult | { error: string }> => {
  const { data, error } = await supabase.functions.invoke('email-dns-guide', { body: {} });
  if (error) return { error: error.message };
  if (data?.error) return { error: data.error };
  return data as DnsGuideResult;
};

/**
 * Change where DMARC aggregate reports are sent. Publishing a `rua` that points
 * at a non-existent mailbox throws away every report, so this is deliberately
 * operator-editable instead of hardcoded.
 */
export const setDmarcRua = async (id: number, address: string) => {
  const clean = address.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
    return { error: 'Enter a valid email address.' };
  }
  const { error } = await supabase.from('mkt_email_domain_config').update({ dmarc_rua: clean }).eq('id', id);
  return error ? { error: error.message } : { success: true };
};

export const actionLabel = (a: RecordAction, es = false): string =>
  a === 'add'
    ? es
      ? 'AGREGAR nuevo'
      : 'ADD new record'
    : a === 'replace'
      ? es
        ? 'REEMPLAZAR el existente'
        : 'REPLACE existing'
      : a === 'already-correct'
        ? es
          ? 'YA CORRECTO'
          : 'ALREADY CORRECT'
        : es
          ? 'NO AGREGAR'
          : 'DO NOT ADD';

export const actionTone = (a: RecordAction): string =>
  a === 'add'
    ? 'bg-blue-50 text-blue-700 ring-blue-200'
    : a === 'replace'
      ? 'bg-orange-50 text-orange-700 ring-orange-200'
      : a === 'already-correct'
        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
        : 'bg-slate-100 text-slate-600 ring-slate-200';

/**
 * The step-by-step click path inside GoDaddy, kept here so the UI and any docs
 * stay in sync.
 */
export const GODADDY_STEPS_EN = [
  'Sign in at godaddy.com and open My Products.',
  'Find giggone.com → click DNS (or Manage DNS).',
  'You land on the DNS Records list for giggone.com.',
  'For a REPLACE: find the row with the same Type + Name, click the pencil (Edit), paste the new value, Save.',
  'For an ADD: click "Add New Record", choose the Type, paste the Name and Value exactly, set TTL, Save.',
  'Touch ONLY the rows listed below. Leave Microsoft 365 (root MX, selector1/selector2, autodiscover) and every mail.giggone.com row (GoHighLevel) exactly as they are.',
  'Publish, then wait ~30 minutes and press "Re-check DNS now" — or just let the hourly job pick it up.',
];

export const GODADDY_STEPS_ES = [
  'Inicia sesión en godaddy.com y abre Mis productos.',
  'Busca giggone.com → haz clic en DNS (o Administrar DNS).',
  'Verás la lista de registros DNS de giggone.com.',
  'Para REEMPLAZAR: busca la fila con el mismo Tipo + Nombre, haz clic en el lápiz (Editar), pega el nuevo valor y Guarda.',
  'Para AGREGAR: haz clic en "Agregar nuevo registro", elige el Tipo, pega el Nombre y el Valor exactos, define el TTL y Guarda.',
  'Modifica SOLO las filas de abajo. Deja intactos Microsoft 365 (MX raíz, selector1/selector2, autodiscover) y todas las filas de mail.giggone.com (GoHighLevel).',
  'Publica, espera ~30 minutos y pulsa "Volver a verificar DNS" — o deja que la tarea automática lo detecte.',
];


export const priorityTone = (p: string): string =>
  p === 'critical'
    ? 'bg-red-50 text-red-700 ring-red-200'
    : p === 'high'
      ? 'bg-orange-50 text-orange-700 ring-orange-200'
      : 'bg-amber-50 text-amber-700 ring-amber-200';

export const authTone = (s?: string): string =>
  s === 'pass'
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : s === 'warn'
      ? 'bg-amber-50 text-amber-700 ring-amber-200'
      : 'bg-red-50 text-red-700 ring-red-200';

export const authLabel = (s?: string): string => (s === 'pass' ? 'PASS' : s === 'warn' ? 'PARTIAL' : 'FAIL');

/* ───────────────────────────────────────────────────────────────────────────
 * LIVE INBOX TEST
 *
 * A real message, through the real production sender, to real seed mailboxes —
 * then the RECEIVING server's own SMTP answer is read back out of the provider
 * event log. That is the only honest evidence a sending system can produce.
 *
 * IMPORTANT AND DELIBERATE: folder placement (Inbox / Promotions / Spam) is
 * decided AFTER the receiver accepts the message and is never reported back to
 * the sender — Gmail returns the same "250 OK" whether it files the mail in the
 * Inbox or in Spam. So this module never claims to know the folder. It returns
 * the acceptance evidence plus the exact header check the operator runs in the
 * seed mailbox.
 * ─────────────────────────────────────────────────────────────────────────── */

export type InboxVerdict = 'delivered' | 'accepted' | 'deferred' | 'rejected' | 'pending' | 'send_failed';

export interface SendingIdentity {
  activeFromDomain: string;
  branded: boolean;
  support: string;
  noreply: string;
  signsAs: string;
  transport: string;
  promotedAt: string | null;
  previousFromDomain?: string;
  alignment: { spf: boolean; dkim: boolean; dmarc: boolean; identities: boolean };
}

export interface InboxTestResult {
  to: string;
  provider: string;
  kind: string;
  ok?: boolean;
  suppressed?: boolean;
  messageId: string | null;
  from: string | null;
  subject: string | null;
  signingDomain: string | null;
  transport?: string | null;
  spamScore?: number | null;
  spamFlags?: string[];
  verdict: InboxVerdict;
  acceptedByProvider?: boolean;
  /** The receiving MX that answered, e.g. gmail-smtp-in.l.google.com */
  mxHost: string | null;
  smtpCode: number | null;
  /** The receiver's own words, e.g. "250 2.0.0 OK …" */
  smtpResponse: string | null;
  sendingIp?: string | null;
  envelopeSender?: string | null;
  sessionSeconds?: number | null;
  at: string | null;
  eventsSeen?: string[];
  error?: string | null;
}

export interface HeaderCheck {
  how: string;
  expect: string[];
  folder: string;
}

export interface LiveTestResponse {
  success: boolean;
  action: 'probe' | 'send' | 'selftest' | 'status';
  testedAt?: string;
  template?: string;
  sendingIdentity: SendingIdentity;
  previews?: {
    template: string;
    from: string | null;
    envelopeFrom: string | null;
    signingDomain: string | null;
    transport: string | null;
    dkimAligned: boolean;
    alignmentVerified: boolean;
    spamScore: number | null;
    spamFlags: string[];
  }[];
  results?: InboxTestResult[];
  summary?: { attempted: number; delivered: number; pending: number; rejected: number; failedToSend: number };
  headerCheck: HeaderCheck;
  limitations: string[];
  next?: string;
  error?: string;
}

/** No-send check: which From: identity is live right now. */
export const probeSendingIdentity = async (): Promise<LiveTestResponse | { error: string }> => {
  const { data, error } = await supabase.functions.invoke('email-live-test', { body: { action: 'probe' } });
  if (error) return { error: error.message };
  if (data?.error) return { error: data.error };
  return data as LiveTestResponse;
};

/** Real send to the operator's seed addresses (administrator only, server-side). */
export const runLiveInboxTest = async (
  recipients: string[],
  template = 'account_activated'
): Promise<LiveTestResponse | { error: string }> => {
  const clean = recipients.map((r) => r.trim().toLowerCase()).filter(Boolean);
  if (!clean.length) return { error: 'Add at least one seed address.' };
  const bad = clean.find((r) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r));
  if (bad) return { error: `"${bad}" is not a valid email address.` };
  const { data, error } = await supabase.functions.invoke('email-live-test', {
    body: { action: 'send', recipients: clean, template },
  });
  if (error) return { error: error.message };
  if (data?.error) return { error: data.error };
  return data as LiveTestResponse;
};

/** Re-poll the provider event log for messages already sent. */
export const pollInboxTest = async (messageIds: string[] = []): Promise<LiveTestResponse | { error: string }> => {
  const { data, error } = await supabase.functions.invoke('email-live-test', {
    body: { action: 'status', messageIds },
  });
  if (error) return { error: error.message };
  if (data?.error) return { error: data.error };
  return data as LiveTestResponse;
};

export const verdictTone = (v?: InboxVerdict): string =>
  v === 'delivered'
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : v === 'accepted'
      ? 'bg-teal-50 text-teal-700 ring-teal-200'
      : v === 'pending'
        ? 'bg-slate-100 text-slate-600 ring-slate-200'
        : v === 'deferred'
          ? 'bg-amber-50 text-amber-700 ring-amber-200'
          : 'bg-red-50 text-red-700 ring-red-200';

export const verdictLabel = (v?: InboxVerdict, es = false): string => {
  switch (v) {
    case 'delivered':
      return es ? 'ENTREGADO' : 'DELIVERED';
    case 'accepted':
      return es ? 'ACEPTADO' : 'ACCEPTED';
    case 'deferred':
      return es ? 'REINTENTANDO' : 'DEFERRED';
    case 'rejected':
      return es ? 'RECHAZADO' : 'REJECTED';
    case 'send_failed':
      return es ? 'NO ENVIADO' : 'SEND FAILED';
    default:
      return es ? 'ESPERANDO' : 'PENDING';
  }
};

/** Seed addresses we suggest so the operator covers the two receivers that matter. */
export const SEED_HINTS = [
  { label: 'Gmail', placeholder: 'yourname@gmail.com' },
  { label: 'Outlook / Hotmail', placeholder: 'yourname@outlook.com' },
];
